import React, { useEffect, useRef, useState } from 'react';
import type { AudioSettings, KeySignature, TimeSignature } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// DesktopHeader — EDIT MODE
// Figma source: WmN2tKfRSxv1PAWTdQBkrV node 538:620
// Root layoutMode: none → flex with centered middle section
// ─────────────────────────────────────────────────────────────────────────────

const HEADER_H = 64;
const KUMBH = 'Kumbh Sans, sans-serif';
const ZONE_BG = '#3D3028';
const PILL_BG = '#D9D9D9';
const TEXT_LIGHT = '#D3CCBD';
const TEXT_DARK  = '#271B13';
const FIELD_BG = '#F4F1EB';
const FIELD_TEXT = '#DB9817';
const SOUND_BG = 'rgba(157, 219, 23, 0.46)';
const SOUND_TITLE = '#89A947';
const RHYTHM_BG = 'rgba(83, 210, 212, 0.71)';
const RHYTHM_TITLE = '#35A8A8';
const SECTION_BORDER = '#B7B7BB';

const SONG_STYLES = ['Jazz', 'Swing', 'Latin', 'Ballad', 'Soul', 'Pop', 'Rock', 'Bossa', 'Country', 'Reggae'] as const;
const KEY_SIGNATURES: KeySignature[] = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#', 'F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'];
const TIME_SIGNATURES = [2, 3, 4, 5, 6] as const;

type OpenPanel = 'title' | 'rhythm' | null;

interface FloatingPanelState {
  left: number;
  top: number;
  width: number;
}

interface DesktopHeaderProps {
  songTitle: string;
  onTitleChange: (v: string) => void;
  songAuthor: string;
  onAuthorChange: (v: string) => void;
  songStyle: string;
  onStyleChange: (v: string) => void;
  isPlaying: boolean;
  onPlayPause: () => void;
  onStop: () => void;
  onRewind: () => void;
  transposeAmount: number;
  onTranspose: (delta: number) => void;
  onNewChart: () => void;
  onOpenChart: () => void;
  onSetlists: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onExportPDF: () => void;
  onJamSession: () => void;
  onGigMode: () => void;
  onHome: () => void;
  onPianoRec?: () => void;
  onHelp: () => void;
  onAccount: () => void;
  onSettings: () => void;
  numberOfMeasures: number;
  onNumMeasuresChange: (value: number) => void;
  timeSignature: TimeSignature;
  onTimeSignatureChange: (beats: number) => void;
  keySignature: KeySignature;
  onKeySignatureChange: (key: KeySignature) => void;
  audioSettings: AudioSettings;
  onAudioSettingsChange: (settings: AudioSettings) => void;
}

const MENU_ITEMS = [
  { label: 'NEW',            icon: '/figma-assets/edit-mode/icon-new.svg',            iconW: 35, iconH: 35 },
  { label: 'OPEN',           icon: '/figma-assets/edit-mode/icon-open.svg',           iconW: 35, iconH: 35 },
  { label: 'SETLISTS',       icon: '/figma-assets/edit-mode/icon-setlists.svg',       iconW: 29, iconH: 29 },
  { label: 'PIANO REC',      icon: '/figma-assets/edit-mode/icon-piano-rec.svg',      iconW: 29, iconH: 29 },
  { label: 'JAM SESSION',    icon: '/figma-assets/edit-mode/icon-jam.svg',            iconW: 25, iconH: 29 },
  { label: 'GIG MODE',       icon: '/figma-assets/edit-mode/icon-gig.svg',            iconW: 29, iconH: 29 },
  { label: 'SAVE',           icon: '/figma-assets/edit-mode/icon-save.svg',           iconW: 27, iconH: 27 },
  { label: 'SAVE as',        icon: '/figma-assets/edit-mode/icon-save-as.svg',        iconW: 37, iconH: 30 },
  { label: 'EXPORT PDF',     icon: '/figma-assets/edit-mode/icon-export-pdf.svg',     iconW: 35, iconH: 28 },
  { label: 'HOME',           icon: '/figma-assets/edit-mode/icon-home.svg',           iconW: 27, iconH: 29 },
] as const;

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  songTitle, onTitleChange, songAuthor, onAuthorChange, songStyle, onStyleChange,
  isPlaying, onPlayPause, onStop, onRewind, transposeAmount, onTranspose,
  onNewChart, onOpenChart, onSetlists, onSave, onSaveAs, onExportPDF,
  onJamSession, onGigMode, onHome, onPianoRec,
  onHelp, onAccount, onSettings,
  numberOfMeasures, onNumMeasuresChange,
  timeSignature, onTimeSignatureChange,
  keySignature, onKeySignatureChange,
  audioSettings, onAudioSettingsChange,
}) => {
  const [isMenuOpen,    setIsMenuOpen]    = useState(false);
  const [isBtnHovered,  setIsBtnHovered]  = useState(false);
  const [isBtnPressed,  setIsBtnPressed]  = useState(false);
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  const [panelState, setPanelState] = useState<FloatingPanelState>({ left: 16, top: 72, width: 420 });
  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const rhythmButtonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setIsMenuOpen(false);
  const mi = (fn: () => void) => () => { fn(); closeMenu(); };

  const actions: Record<string, () => void> = {
    'NEW':            mi(onNewChart),
    'OPEN':           mi(onOpenChart),
    'SETLISTS':       mi(onSetlists),
    'PIANO REC':      mi(onPianoRec ?? (() => {})),
    'JAM SESSION':    mi(onJamSession),
    'GIG MODE':       mi(onGigMode),
    'SAVE':           mi(onSave),
    'SAVE as':        mi(onSaveAs),
    'EXPORT PDF':     mi(onExportPDF),
    'HOME':           mi(onHome),
  };

  const buildPanelState = (rect: DOMRect): FloatingPanelState => {
    const viewportWidth = window.innerWidth;
    const horizontalInset = 16;
    const maxWidth = viewportWidth - horizontalInset * 2;
    const width = Math.min(maxWidth, rect.width);
    const left = Math.min(
      Math.max(horizontalInset, rect.left + (rect.width - width) / 2),
      viewportWidth - width - horizontalInset,
    );

    return {
      left,
      top: rect.bottom + 8,
      width,
    };
  };

  const syncPanelPosition = (panel: Exclude<OpenPanel, null>) => {
    const anchor = panel === 'title' ? titleButtonRef.current : rhythmButtonRef.current;
    if (!anchor) return;
    setPanelState(buildPanelState(anchor.getBoundingClientRect()));
  };

  const togglePanel = (panel: Exclude<OpenPanel, null>) => {
    if (openPanel === panel) {
      setOpenPanel(null);
      return;
    }
    syncPanelPosition(panel);
    setOpenPanel(panel);
  };

  useEffect(() => {
    if (!openPanel) return;

    const handleReposition = () => syncPanelPosition(openPanel);
    handleReposition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [openPanel]);

  const btnTransform = isBtnPressed ? 'scale(0.93)' : isBtnHovered ? 'scale(1.06)' : 'scale(1)';
  const btnFilter    = isBtnHovered && !isBtnPressed ? 'brightness(1.18)' : 'brightness(1)';
  const isTitleOpen = openPanel === 'title';
  const isRhythmOpen = openPanel === 'rhythm';
  const isCompactPanel = panelState.width <= 180;
  const compactLabelFont = isCompactPanel ? 12 : 18;
  const compactFieldFont = isCompactPanel ? 12 : 18;
  const rhythmLabelFont = isCompactPanel ? 13 : 24;
  const rhythmFieldFont = isCompactPanel ? 13 : 24;
  const rhythmHeaderFont = isCompactPanel ? 10 : 16;
  const rhythmGridTemplate = '1fr';
  const fieldMinHeight = isCompactPanel ? 24 : 36;
  const panelPadding = isCompactPanel ? '8px 8px 10px' : '12px';
  const sectionPadding = isCompactPanel ? '8px 8px 10px' : '10px 12px 12px';

  const updateAudioSetting = <K extends keyof AudioSettings>(key: K, value: AudioSettings[K]) => {
    onAudioSettingsChange({
      ...audioSettings,
      [key]: value,
    });
  };

  return (
    <>
      {/* Menu backdrop */}
      {isMenuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 109 }} onClick={closeMenu} aria-hidden="true" />
      )}

      {/* Menu slide-in panel — #572:209 logic, starts below header */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          position: 'fixed',
          left: 0,
          top: HEADER_H,
          width: 343,
          height: 628,
          zIndex: 110,
          backgroundColor: ZONE_BG,
          borderRadius: 12,
          boxShadow: '4px 4px 7px rgba(0,0,0,0.37)',
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(-110%)',
          opacity: isMenuOpen ? 1 : 0,
          transition: 'transform 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease',
          pointerEvents: isMenuOpen ? 'auto' : 'none',
        }}
      >
        <img
          src="/figma-assets/edit-mode/menu-logo.svg"
          width={51} height={51}
          alt="Chord Notator"
          style={{ position: 'absolute', left: 19, top: 16, display: 'block' }}
        />
        {MENU_ITEMS.map((item, idx) => (
          <button
            key={item.label}
            onClick={actions[item.label]}
            style={{
              position: 'absolute',
              left: 77,
              top: 81 + idx * 54,
              width: 250, height: 35,
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <img src={item.icon} width={item.iconW} height={item.iconH} alt="" style={{ flexShrink: 0 }} />
            <span style={{ fontFamily: KUMBH, fontWeight: 500, fontSize: 20, color: TEXT_LIGHT, whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
          </button>
        ))}
      </div>

      {/* Header bar */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: HEADER_H,
          backgroundColor: '#231E1B',
          zIndex: 100,
          overflow: 'visible',
          display: 'grid',
          gridTemplateColumns: 'auto minmax(150.98px, 1fr) auto minmax(150.98px, 1fr) auto',
          alignItems: 'center',
          columnGap: 'clamp(10px, 1.6vw, 28px)',
          padding: '0 clamp(16px, 2.9vw, 42px) 0 clamp(16px, 3.5vw, 50px)',
          boxSizing: 'border-box',
        }}
      >

        <div style={{ display: 'flex', alignItems: 'center', gap: 12.3, minWidth: 0 }}>
          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            onMouseEnter={() => setIsBtnHovered(true)}
            onMouseLeave={() => { setIsBtnHovered(false); setIsBtnPressed(false); }}
            onMouseDown={() => setIsBtnPressed(true)}
            onMouseUp={() => setIsBtnPressed(false)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '10.368px',
              backgroundColor: ZONE_BG,
              borderRadius: 7.776,
              border: 'none', cursor: 'pointer',
              transform: btnTransform,
              filter: btnFilter,
              transition: 'transform 0.15s ease, filter 0.15s ease',
              zIndex: 101,
              flexShrink: 0,
              boxShadow: '2.592px 2.592px 3.564px 0.648px rgba(0, 0, 0, 0.25)',
            }}
          >
            <img
              src={isMenuOpen
                ? '/figma-assets/edit-mode/menu-btn-open.svg'
                : '/figma-assets/edit-mode/menu-btn.svg'}
              width={30.52} height={30.52}
              alt=""
              style={{ display: 'block' }}
            />
          </button>

          <div
            style={{
              flexShrink: 0,
              width: 121.82,
              padding: '14.256px 6.48px',
              backgroundColor: ZONE_BG,
              borderRadius: 7.776,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8.82,
            }}
          >
            <img src="/figma-assets/edit-mode/icon-edit-mode.svg" width={22.68} height={22.68} alt="" style={{ flexShrink: 0 }} />
            <span style={{
              fontFamily: KUMBH,
              fontWeight: 500,
              fontSize: 12.96,
              lineHeight: '1em',
              color: TEXT_LIGHT,
              whiteSpace: 'nowrap',
              display: 'block',
            }}>
              Edit Mode
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            ref={titleButtonRef}
            onClick={() => togglePanel('title')}
            aria-expanded={isTitleOpen}
            aria-label="Edit song title"
            style={{
              position: 'relative',
              width: 150.98,
              height: 19.44,
              backgroundColor: isTitleOpen ? FIELD_BG : PILL_BG,
              borderRadius: isTitleOpen ? '14.904px 14.904px 0 0' : 14.904,
              border: 'none',
              cursor: 'pointer',
              padding: '0 18px 0 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              boxShadow: isTitleOpen ? '0px 6px 9.7px rgba(0,0,0,0.11)' : 'none',
              zIndex: isTitleOpen ? 201 : 'auto',
            }}
          >
              <span style={{
                width: '100%',
                fontFamily: KUMBH,
                fontWeight: 400,
                fontSize: 12.96,
                lineHeight: '1.25em',
                color: TEXT_DARK,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {songTitle || 'Title'}
              </span>
              <img
                src="/figma-assets/edit-mode/icon-arrow-up.svg"
                width={12.65} height={12.65} alt=""
                style={{
                  position: 'absolute',
                  right: 4.2,
                  top: '50%',
                  transform: isTitleOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10.37, minWidth: 0 }}>
          <div
            style={{
              height: 51.84,
              padding: '10.368px 16.2px',
              backgroundColor: ZONE_BG,
              borderRadius: 7.776,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 21.384,
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <button
              onClick={onRewind}
              aria-label="Rewind"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <img src="/figma-assets/edit-mode/icon-rewind.svg" width={37.48} height={26.62} alt="" style={{ display: 'block' }} />
            </button>
            <button
              onClick={isPlaying ? onStop : onPlayPause}
              aria-label={isPlaying ? 'Stop' : 'Play'}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <img
                src={isPlaying ? '/figma-assets/icons/stop-circle.svg' : '/figma-assets/edit-mode/icon-play.svg'}
                width={32.46} height={32.46}
                alt=""
                style={{ display: 'block' }}
              />
            </button>
          </div>

          <div
            style={{
              width: 101.74,
              padding: '5.832px 12.96px',
              backgroundColor: ZONE_BG,
              borderRadius: 7.776,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              <span style={{
                fontFamily: KUMBH, fontWeight: 400, fontSize: 7.1604,
                textTransform: 'uppercase', letterSpacing: '0.42em',
                color: TEXT_LIGHT, opacity: 0.53,
                whiteSpace: 'nowrap', display: 'block', width: '100%', textAlign: 'center',
                lineHeight: '1.25em',
              }}>
                transpose
              </span>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <button onClick={() => onTranspose(-1)} aria-label="Transpose down"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <img src="/figma-assets/edit-mode/icon-minus.svg" width={28.64} height={28.64} alt="" style={{ display: 'block' }} />
                </button>
                <span style={{
                  fontFamily: KUMBH, fontWeight: 400, fontSize: 14.3208,
                  lineHeight: '1.25em',
                  color: TEXT_LIGHT, textAlign: 'center', minWidth: 18,
                }}>
                  {transposeAmount}
                </span>
                <button onClick={() => onTranspose(1)} aria-label="Transpose up"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <img src="/figma-assets/edit-mode/icon-plus.svg" width={28.64} height={28.64} alt="" style={{ display: 'block' }} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            ref={rhythmButtonRef}
            onClick={() => {
              togglePanel('rhythm');
            }}
            aria-expanded={isRhythmOpen}
            aria-label="Open Rhythm & Sound settings"
            style={{
              position: 'relative',
              width: 150.98,
              height: 19.44,
              backgroundColor: isRhythmOpen ? FIELD_BG : PILL_BG,
              borderRadius: isRhythmOpen ? '14.904px 14.904px 0 0' : 14.904,
              border: 'none',
              cursor: 'pointer',
              padding: '0 18px 0 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              boxShadow: isRhythmOpen ? '0px 6px 9.7px rgba(0,0,0,0.11)' : 'none',
              zIndex: isRhythmOpen ? 201 : 'auto',
            }}
          >
            <span style={{
              width: '100%',
              fontFamily: KUMBH,
              fontWeight: 400,
              fontSize: 12.96,
              lineHeight: '1.25em',
              color: TEXT_DARK,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              Rhythm &amp; Sound
            </span>
            <img
              src="/figma-assets/edit-mode/icon-arrow-up.svg"
              width={12.76} height={12.76} alt=""
              style={{
                position: 'absolute',
                right: 3.66,
                top: '50%',
                transform: isRhythmOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                transition: 'transform 0.2s ease',
              }}
            />
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div
            style={{
              backgroundColor: ZONE_BG,
              borderRadius: 7.776,
              padding: '18.792px 18.144px',
              display: 'flex',
              alignItems: 'center',
              gap: 25.92,
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
              <button onClick={onAccount} aria-label="Account" title="Account"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <img src="/figma-assets/edit-mode/icon-account.svg" width={14.47} height={14.47} alt="" style={{ display: 'block' }} />
              </button>
              <button onClick={onSettings} aria-label="Settings" title="Settings"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <img src="/figma-assets/edit-mode/icon-settings.svg" width={14.47} height={14.47} alt="" style={{ display: 'block' }} />
              </button>
              <button onClick={onHelp} aria-label="Help" title="Help"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <img src="/figma-assets/edit-mode/icon-help.svg" width={14.47} height={14.47} alt="" style={{ display: 'block' }} />
              </button>
            </div>
          </div>
      </div>

      {openPanel && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenPanel(null)} aria-hidden="true" />
          {isTitleOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: panelState.left,
                top: panelState.top,
                width: panelState.width,
                zIndex: 200,
                backgroundColor: PILL_BG,
                borderRadius: '0 0 23px 23px',
                padding: isCompactPanel ? '10px 8px 12px' : '12px 16px 18px',
                boxShadow: '0px 6px 9.7px rgba(0,0,0,0.11)',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: isCompactPanel ? 8 : 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: isCompactPanel ? 6 : 12, alignItems: 'center' }}>
                  <span style={{ fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Title :</span>
                  <div style={{ borderRadius: 15, backgroundColor: FIELD_BG, padding: isCompactPanel ? '4px 8px' : '6px 12px' }}>
                    <input
                      type="text"
                      value={songTitle}
                      onChange={(e) => onTitleChange(e.target.value)}
                      autoFocus
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactFieldFont, color: FIELD_TEXT, boxSizing: 'border-box' }}
                      placeholder="Song name"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: isCompactPanel ? 6 : 12, alignItems: 'center' }}>
                  <span style={{ fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Author :</span>
                  <div style={{ borderRadius: 15, backgroundColor: FIELD_BG, padding: isCompactPanel ? '4px 8px' : '6px 12px' }}>
                    <input
                      type="text"
                      value={songAuthor}
                      onChange={(e) => onAuthorChange(e.target.value)}
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactFieldFont, color: FIELD_TEXT, boxSizing: 'border-box' }}
                      placeholder="Author name"
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: isCompactPanel ? 6 : 12, alignItems: 'center' }}>
                  <span style={{ fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Style :</span>
                  <div style={{ borderRadius: 15, backgroundColor: FIELD_BG, padding: isCompactPanel ? '4px 8px' : '6px 12px' }}>
                    <select
                      value={songStyle}
                      onChange={(e) => onStyleChange(e.target.value)}
                      style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontVariantCaps: 'small-caps', fontSize: compactFieldFont, color: FIELD_TEXT, boxSizing: 'border-box', appearance: 'none' }}
                    >
                      {SONG_STYLES.map((styleName) => (
                        <option key={styleName} value={styleName}>{styleName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button disabled aria-label="More metadata" style={{ background: 'none', border: 'none', cursor: 'default', opacity: 0.4, padding: 0 }}>
                    <img src="/figma-assets/icons/plus-circle.svg" width={32} height={32} alt="" />
                  </button>
                </div>
              </div>
            </div>
          )}
          {isRhythmOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: panelState.left,
                top: panelState.top,
                width: panelState.width,
                zIndex: 200,
                backgroundColor: PILL_BG,
                borderRadius: '0 0 23px 23px',
                padding: panelPadding,
                boxShadow: '0px 6px 9.7px rgba(0,0,0,0.11)',
                boxSizing: 'border-box',
                maxHeight: 'calc(100vh - 88px)',
                overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: isCompactPanel ? 8 : 10 }}>
                <div style={{ backgroundColor: SOUND_BG, borderRadius: 13, border: `2px solid ${SECTION_BORDER}`, padding: sectionPadding }}>
                  <div style={{ fontFamily: KUMBH, fontWeight: 900, fontSize: rhythmHeaderFont, letterSpacing: isCompactPanel ? '0.42em' : '0.72em', textTransform: 'uppercase', color: SOUND_TITLE, textAlign: 'center', marginBottom: isCompactPanel ? 6 : 8, lineHeight: 1.1 }}>sound</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isCompactPanel ? 6 : 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Key</span>
                      <div style={{ position: 'relative', minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15 }}>
                        <select value={keySignature} onChange={(e) => onKeySignatureChange(e.target.value as KeySignature)} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: isCompactPanel ? 10 : 17, paddingRight: isCompactPanel ? 28 : 46, fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT, appearance: 'none', cursor: 'pointer' }}>
                          {KEY_SIGNATURES.map((key) => (
                            <option key={key} value={key}>{key}</option>
                          ))}
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" alt="" style={{ position: 'absolute', right: isCompactPanel ? 6 : 10, top: '50%', width: isCompactPanel ? 14 : 24, height: isCompactPanel ? 14 : 24, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Timbre:</span>
                      <div style={{ position: 'relative', minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15 }}>
                        <select value={audioSettings.timbre} onChange={(e) => updateAudioSetting('timbre', e.target.value as AudioSettings['timbre'])} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: isCompactPanel ? 10 : 17, paddingRight: isCompactPanel ? 28 : 46, fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT, appearance: 'none', cursor: 'pointer' }}>
                          <option value="Piano">Piano</option>
                          <option value="Sine">Sine Wave</option>
                          <option value="Synth">Synth</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" alt="" style={{ position: 'absolute', right: isCompactPanel ? 6 : 10, top: '50%', width: isCompactPanel ? 14 : 24, height: isCompactPanel ? 14 : 24, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Voicing:</span>
                      <div style={{ position: 'relative', minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15 }}>
                        <select value={audioSettings.voicing} onChange={(e) => updateAudioSetting('voicing', e.target.value as AudioSettings['voicing'])} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: isCompactPanel ? 10 : 17, paddingRight: isCompactPanel ? 28 : 46, fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT, appearance: 'none', cursor: 'pointer' }}>
                          <option value="Spread">Spread</option>
                          <option value="Stacked">Stacked</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" alt="" style={{ position: 'absolute', right: isCompactPanel ? 6 : 10, top: '50%', width: isCompactPanel ? 14 : 24, height: isCompactPanel ? 14 : 24, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Dynamics:</span>
                      <div style={{ position: 'relative', minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15 }}>
                        <select value={audioSettings.dynamics} onChange={(e) => updateAudioSetting('dynamics', e.target.value as AudioSettings['dynamics'])} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: isCompactPanel ? 10 : 17, paddingRight: isCompactPanel ? 28 : 46, fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT, appearance: 'none', cursor: 'pointer' }}>
                          <option value="Block">Block</option>
                          <option value="Arpeggiated">Arpeggiated</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" alt="" style={{ position: 'absolute', right: isCompactPanel ? 6 : 10, top: '50%', width: isCompactPanel ? 14 : 24, height: isCompactPanel ? 14 : 24, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ backgroundColor: RHYTHM_BG, borderRadius: 13, border: `2px solid ${SECTION_BORDER}`, padding: sectionPadding }}>
                  <div style={{ fontFamily: KUMBH, fontWeight: 900, fontSize: rhythmHeaderFont, letterSpacing: isCompactPanel ? '0.42em' : '0.72em', textTransform: 'uppercase', color: RHYTHM_TITLE, textAlign: 'center', marginBottom: isCompactPanel ? 6 : 8, lineHeight: 1.1 }}>rythm</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: isCompactPanel ? 6 : 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Measure</span>
                      <div style={{ minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15, padding: isCompactPanel ? '0 10px' : '0 17px', display: 'flex', alignItems: 'center' }}>
                        <input type="number" min="1" value={numberOfMeasures} onChange={(e) => onNumMeasuresChange(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>Time sig</span>
                      <div style={{ position: 'relative', minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15 }}>
                        <select value={timeSignature.beats} onChange={(e) => onTimeSignatureChange(parseInt(e.target.value, 10))} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none', paddingLeft: isCompactPanel ? 10 : 17, paddingRight: isCompactPanel ? 28 : 46, fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT, appearance: 'none', cursor: 'pointer' }}>
                          {TIME_SIGNATURES.map((beats) => (
                            <option key={beats} value={beats}>{beats}/4</option>
                          ))}
                          <option value="6">6/8</option>
                        </select>
                        <img src="/figma-assets/mobile-accordion/pill-arrow.svg" alt="" style={{ position: 'absolute', right: isCompactPanel ? 6 : 10, top: '50%', width: isCompactPanel ? 14 : 24, height: isCompactPanel ? 14 : 24, transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: rhythmGridTemplate, gap: 8, alignItems: 'center' }}>
                      <span style={{ fontFamily: KUMBH, fontSize: rhythmLabelFont, color: TEXT_DARK, textAlign: 'left' }}>BPM</span>
                      <div style={{ minHeight: fieldMinHeight, backgroundColor: FIELD_BG, borderRadius: 15, padding: isCompactPanel ? '0 10px' : '0 17px', display: 'flex', alignItems: 'center' }}>
                        <input type="number" min="20" max="400" value={audioSettings.bpm} onChange={(e) => updateAudioSetting('bpm', parseInt(e.target.value, 10) || 120)} style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontFamily: KUMBH, fontSize: rhythmFieldFont, color: FIELD_TEXT }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DesktopHeader;
